
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { translations } from '../i18n/translations';
import { Language } from '../types';

interface HeaderProps {
  lang: Language;
  setLang: (l: Language) => void;
}

export const Header: React.FC<HeaderProps> = ({ lang, setLang }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const t = translations[lang].nav;
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => {
    setLang(lang === 'pt' ? 'en' : 'pt');
  };

  const menuItems = [
    { name: t.units, path: '/unidades' },
    { name: t.management, path: '/gestao' },
    { name: t.contact, path: '/contactos' },
  ];

  // Configuração estática: Banner sempre branco para garantir visibilidade do logo
  const headerBg = "bg-white border-b border-slate-100 py-3 md:py-4 shadow-sm";
  const textColor = "text-slate-900";
  const logoFilter = "brightness-100";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src="/logo.png"
            alt="Casas do Paço" 
            className={`h-10 md:h-14 w-auto transition-all ${logoFilter}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center space-x-8">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm font-semibold tracking-wide hover:text-amber-600 transition-colors ${textColor}`}
            >
              {item.name}
            </Link>
          ))}
          
          <div className="flex items-center gap-4 pl-4 border-l border-slate-200">
            <button 
              onClick={toggleLang}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border border-slate-200 transition-all text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900`}
            >
              <Globe size={14} />
              {lang.toUpperCase()}
            </button>
            
            {/* <Link to="/login" title="Painel Admin" className={`opacity-40 hover:opacity-100 transition-opacity text-slate-900`}>
              <Lock size={16} />
            </Link> */}

            <Link to="/reservas">
              <button className="bg-slate-900 hover:bg-amber-600 text-white text-sm font-bold px-7 py-3 rounded-full transition-all shadow-lg hover:shadow-amber-600/20 transform hover:-translate-y-0.5">
                {t.book}
              </button>
            </Link>
          </div>
        </nav>

        <div className="lg:hidden flex items-center space-x-4">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`text-slate-900 p-1`}
            aria-label={
              isMenuOpen
                ? lang === "pt" ? "Fechar menu" : "Close menu"
                : lang === "pt" ? "Abrir menu" : "Open menu"
            }
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl border-t border-slate-100 animate-fade-in-down">
          <div className="flex flex-col p-6 space-y-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className="text-lg font-bold text-slate-900 border-b border-slate-50 pb-3"
              >
                {item.name}
              </Link>
            ))}
            <div className="flex items-center justify-between py-2">
               <button
                onClick={toggleLang}
                className="flex items-center gap-2 text-sm font-bold text-slate-500"
              >
                <Globe size={18} /> {lang === 'pt' ? 'Português' : 'English'}
              </button>
            </div>
            <Link
              to="/reservas"
              onClick={() => setIsMenuOpen(false)}
              className="bg-slate-900 text-white text-center py-4 rounded-xl font-bold text-lg shadow-xl"
            >
              {t.book}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
